<section class="route-planner"
         [ngClass]="{'route-planner--compact': compact}"
         [attr.aria-labelledby]="compact ? null : 'route-planner-title'"
         [attr.aria-label]="compact ? 'Расчёт автомобильного маршрута' : null">
    <header class="route-planner__header" *ngIf="!compact">
        <div>
            <div class="route-planner__eyebrow">Маршрут по дорогам</div>
            <h2 id="route-planner-title">Расстояние между городами на автомобиле</h2>
            <p>Выберите города — рассчитаем реальный автомобильный маршрут, время в пути и километраж.</p>
        </div>
        <span class="route-planner__privacy">Маршрут по дорогам</span>
    </header>

    <div class="route-planner__fields">
        <div class="route-planner__combobox">
            <label [for]="'route-' + context + '-origin'">Откуда</label>
            <div class="route-planner__input-wrap">
                <input
                    [id]="'route-' + context + '-origin'"
                    type="text"
                    inputmode="search"
                    autocomplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    [attr.aria-expanded]="hasSuggestions('origin')"
                    [attr.aria-controls]="'route-' + context + '-origin-list'"
                    [attr.aria-activedescendant]="activeOptionId('origin')"
                    placeholder="Например, Москва"
                    [ngModel]="originQuery"
                    (ngModelChange)="onQueryChange('origin', $event)"
                    (keydown)="onKeydown('origin', $event)"
                    (blur)="closeSuggestions('origin')"
                >
                <span class="route-planner__spinner" *ngIf="isLoading('origin')" aria-label="Ищем города"></span>
            </div>
            <ul class="route-planner__suggestions"
                *ngIf="hasSuggestions('origin')"
                [id]="'route-' + context + '-origin-list'"
                role="listbox">
                <li *ngFor="let place of originSuggestions; let index = index"
                    [id]="optionId('origin', index)"
                    role="option"
                    [attr.aria-selected]="activeOriginIndex === index"
                    [class.route-planner__suggestion--active]="activeOriginIndex === index"
                    (mousedown)="$event.preventDefault()"
                    (click)="selectPlace('origin', place)">
                    <strong>{{ place.name }}</strong>
                    <span>{{ place.label }}</span>
                </li>
            </ul>
        </div>

        <button class="route-planner__swap" type="button" (click)="swapEndpoints()"
                aria-label="Поменять города местами" title="Поменять города местами">
            <span aria-hidden="true">⇄</span>
        </button>

        <div class="route-planner__combobox">
            <label [for]="'route-' + context + '-destination'">Куда</label>
            <div class="route-planner__input-wrap">
                <input
                    [id]="'route-' + context + '-destination'"
                    type="text"
                    inputmode="search"
                    autocomplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    [attr.aria-expanded]="hasSuggestions('destination')"
                    [attr.aria-controls]="'route-' + context + '-destination-list'"
                    [attr.aria-activedescendant]="activeOptionId('destination')"
                    placeholder="Например, Санкт-Петербург"
                    [ngModel]="destinationQuery"
                    (ngModelChange)="onQueryChange('destination', $event)"
                    (keydown)="onKeydown('destination', $event)"
                    (blur)="closeSuggestions('destination')"
                >
                <span class="route-planner__spinner" *ngIf="isLoading('destination')" aria-label="Ищем города"></span>
            </div>
            <ul class="route-planner__suggestions"
                *ngIf="hasSuggestions('destination')"
                [id]="'route-' + context + '-destination-list'"
                role="listbox">
                <li *ngFor="let place of destinationSuggestions; let index = index"
                    [id]="optionId('destination', index)"
                    role="option"
                    [attr.aria-selected]="activeDestinationIndex === index"
                    [class.route-planner__suggestion--active]="activeDestinationIndex === index"
                    (mousedown)="$event.preventDefault()"
                    (click)="selectPlace('destination', place)">
                    <strong>{{ place.name }}</strong>
                    <span>{{ place.label }}</span>
                </li>
            </ul>
        </div>

        <div class="route-planner__actions">
            <button
                class="route-planner__reset"
                type="button"
                (click)="reset()"
                [disabled]="!canReset()"
                aria-label="Сбросить маршрут"
                title="Сбросить маршрут"
            >
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
                    <path d="M4 4v6h6M5.1 15a8 8 0 1 0 .5-7.3L4 10"></path>
                </svg>
            </button>
        </div>
    </div>

    <label
        class="route-planner__round-trip"
        [class.route-planner__round-trip--disabled]="calculating || !canCalculate()"
    >
        <input
            type="checkbox"
            [ngModel]="roundTrip"
            (ngModelChange)="onRoundTripChange($event)"
            [disabled]="calculating || !canCalculate()"
        >
        <span>Туда и обратно</span>
    </label>

    <div class="route-planner__status" aria-live="polite" aria-atomic="true">
        <p class="route-planner__error" *ngIf="error">{{ error }}</p>

        <div
            class="route-planner__map-skeleton"
            *ngIf="calculating && !result"
            role="status"
            aria-label="Строим автомобильный маршрут"
            aria-busy="true"
        ></div>

        <article class="route-planner__result" *ngIf="result" [attr.aria-busy]="calculating">
            <div class="route-planner__summary">
                <strong>{{ formatDistance(result.effective_distance_km) }} км</strong>
                <div class="route-planner__summary-copy">
                    <span class="route-planner__summary-route">{{ result.origin.name }}
                        — {{ result.destination.name }}</span>
                    <p>
                        Примерно {{ durationLabel(result.effective_duration_minutes) }} в пути
                        <span *ngIf="result.round_trip">· учтена обратная дорога</span>
                    </p>
                </div>
            </div>

            <div class="route-planner__badges" *ngIf="result.stale">
                <span>Маршрут построен по сохранённым данным</span>
            </div>

            <app-route-map *ngIf="result.geometry" [result]="result"></app-route-map>
        </article>
    </div>

    <div class="route-planner__popular" *ngIf="popularRoutes.length && !compact">
        <span>Популярные направления</span>
        <button *ngFor="let route of popularRoutes" type="button" (click)="choosePopular(route)">
            {{ route.origin }} — {{ route.destination }}
        </button>
    </div>

</section>
